import { Component, computed, effect, inject } from '@angular/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { TranslateModule } from '@ngx-translate/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatOption } from '@angular/material/autocomplete';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSelect } from '@angular/material/select';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { StickyFooterComponent } from '../sticky-footer/sticky-footer.component';
import { AiAssistantService } from '../../core/services/ai-assistant.service';
import { AIFunction } from '../../core/services/ai-assistant-utils';
import { FunctionParameterInterface, Namespace } from '@vendasta/ai-assistants';
import {
  FunctionHeaderInterface,
  FunctionInterface,
} from '@vendasta/ai-assistants/lib/_internal/interfaces/function.interface';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { CommonModule } from '@angular/common';
import { FunctionParameterComponent, ParameterType } from './function-parameter/function-parameter.component';
import { MatCard, MatCardContent } from '@angular/material/card';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from '../../core/tokens';
import { ActivatedRoute } from '@angular/router';
import { PageService } from '@vendasta/galaxy/page/src/page.service';

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

const FALLBACK_BACK_URL = '../../../assistants';

function httpUrlValidator(): ValidatorFn {
  const urlPattern =
    /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    return urlPattern.test(control.value) ? null : { invalidHttpUrl: true };
  };
}

@Component({
  selector: 'ai-ai-function-form',
  imports: [
    GalaxyPageModule,
    TranslateModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatSelect,
    MatOption,
    MatButton,
    MatIconModule,
    MatIconButton,
    MatMenuModule,
    MatInputModule,
    MatOption,
    StickyFooterComponent,
    CommonModule,
    FunctionParameterComponent,
    MatCard,
    MatCardContent,
  ],
  templateUrl: './ai-function-form.component.html',
  styleUrl: './ai-function-form.component.scss',
})
export class AiFunctionFormComponent {
  private fb = inject(FormBuilder);
  private aiAssistantService = inject(AiAssistantService);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly snackbarService = inject(SnackbarService);
  private readonly pageService = inject(PageService);
  private readonly activeRoute = inject(ActivatedRoute);

  private partnerID = toSignal(this.partnerId$);
  private accountGroupID = toSignal(this.accountGroupId$);
  private nameSpace = computed(() => {
    const accountGroupId = this.accountGroupID();
    const partnerId = this.partnerID();
    if (accountGroupId) {
      return new Namespace({
        accountGroupNamespace: {
          accountGroupId: accountGroupId,
        },
      });
    } else if (partnerId) {
      return new Namespace({
        partnerNamespace: {
          partnerId: partnerId,
        },
      });
    }
    return;
  });
  private aiFunction: Observable<FunctionInterface | null> = this.activeRoute.paramMap.pipe(
    switchMap((paramMap) => {
      const functionId = paramMap.get('functionId');
      if (functionId) {
        return this.aiAssistantService.getFunction(functionId).pipe(
          catchError((err) => {
            console.error(err);
            this.snackbarService.openErrorSnack('AI_ASSISTANT.FUNCTIONS.SNACKBAR.ERROR_FETCHING_FUNCTION');
            return of(null);
          }),
        );
      }
      return of(null);
    }),
  );
  protected readonly function = toSignal(this.aiFunction);

  httpMethodOptions = [
    { value: HttpMethod.GET, label: 'GET' },
    { value: HttpMethod.POST, label: 'POST' },
    { value: HttpMethod.PUT, label: 'PUT' },
    { value: HttpMethod.DELETE, label: 'DELETE' },
    { value: HttpMethod.PATCH, label: 'PATCH' },
  ];

  protected readonly ParameterType = ParameterType;

  commonHeaderOptions = [
    { label: 'Content-Type: JSON', header: { key: 'Content-Type', value: 'application/json' } },
    { label: 'Accept: JSON', header: { key: 'Accept', value: 'application/json' } },
    { label: 'Authorization', header: { key: 'Authorization', value: 'YOUR_TOKEN_HERE' } },
  ];

  protected form: FormGroup = this.fb.group(
    {
      name: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_-]{1,64}$/), Validators.maxLength(64)]],
      description: ['', Validators.required],
      methodType: [HttpMethod.GET, Validators.required],
      url: ['', [Validators.required, httpUrlValidator()]],
      parameters: this.fb.array([]),
      headers: this.fb.array([]),
      generateResponse: [false],
    },
    { updateOn: 'change' },
  );

  get name() {
    return this.form.get('name');
  }

  get description() {
    return this.form.get('description');
  }

  get parameters() {
    return this.form.get('parameters') as FormArray<FormControl>;
  }

  get headers() {
    return this.form.get('headers') as FormArray;
  }

  get methodType() {
    return this.form.get('methodType');
  }

  get url() {
    return this.form.get('url');
  }

  get generateResponse() {
    return this.form.get('generateResponse');
  }

  isGenerateResponseFunction = toSignal(this.generateResponse?.valueChanges || of(false));

  toggleDisableMethodAndUrlEffect = effect(() => {
    const generateResponse = this.isGenerateResponseFunction();
    if (generateResponse) {
      this.methodType?.disable();
      this.url?.disable();
      this.headers.clear();
      const hasOutputParam = this.parameters.controls.some((control) => control.value.name === 'Output');

      if (!hasOutputParam) {
        this.parameters?.insert(
          0,
          this.createParameter({
            name: 'Output',
            type: ParameterType.STRING,
          }),
        );
      }
    } else {
      this.methodType?.enable();
      this.url?.enable();
      if (this.parameters.controls[0]?.value.name === 'Output') this.parameters?.removeAt(0);
    }
  });

  setUpFormEffect = effect(() => {
    const functionData = this.function();
    if (functionData !== null && functionData !== undefined) {
      this.parameters.clear();
      if (functionData.functionParameters && functionData.functionParameters.length > 0) {
        functionData.functionParameters.forEach(() => {
          this.addParameter({});
        });
      }

      this.headers.clear();
      if (functionData.headers && functionData.headers.length > 0) {
        functionData.headers.forEach(() => {
          this.addHeader({});
        });
      }

      this.form.patchValue({
        name: functionData.id,
        description: functionData.description,
        methodType: functionData.methodType,
        url: functionData.url,
        parameters: functionData.functionParameters,
        headers: functionData.headers,
        generateResponse: functionData.generatesAnswer,
      });

      // the name is the function id and is not editable
      this.name?.disable();
      this.form.markAsPristine();
    }
  });

  constructor() {
    this.toggleDisableMethodAndUrlEffect;
    this.setUpFormEffect;
  }

  createParameter(parameter: FunctionParameterInterface) {
    return this.fb.control({
      name: parameter?.name || '',
      type: parameter?.type || '',
      description: parameter?.description,
    });
  }

  addParameter(parameter?: FunctionParameterInterface): void {
    this.parameters.push(this.createParameter(parameter ?? {}));
  }

  removeParameter(index: number): void {
    this.parameters.removeAt(index);
  }

  addHeader(header?: FunctionHeaderInterface): void {
    this.headers.push(
      this.fb.group({
        key: [header?.key || '', Validators.required],
        value: [header?.value || '', Validators.required],
      }),
    );
    this.form.markAsDirty();
  }

  removeHeader(index: number): void {
    this.headers.removeAt(index);
  }

  async submit(): Promise<void> {
    const AIFunction: AIFunction = {
      id: this.name?.value,
      namespace: this.nameSpace(),
      description: this.description?.value,
      methodType: this.methodType?.value,
      functionParameters: this.parameters.value,
      headers: this.headers.value,
      url: this.url?.value,
      generatesAnswer: this.generateResponse?.value,
      authStrategy: {
        unspecified: {},
      },
    };

    if (!this.form.valid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      this.headers.controls.forEach((headerGroup) => {
        if (headerGroup instanceof FormGroup) {
          Object.values(headerGroup.controls).forEach((control) => {
            control.markAsTouched();
          });
        }
      });
      this.snackbarService.openErrorSnack('AI_ASSISTANT.FUNCTIONS.SNACKBAR.MISSING_FIELDS');
      return;
    }
    try {
      await this.aiAssistantService.upsertFunction(AIFunction);
      this.snackbarService.openSuccessSnack('AI_ASSISTANT.FUNCTIONS.SNACKBAR.SUCCESS_SAVING_TOOL');
    } catch (error) {
      console.error(error);
      this.snackbarService.openErrorSnack('AI_ASSISTANT.FUNCTIONS.SNACKBAR.SAVE_ERROR');
    } finally {
      if (this.function()) {
        this.form.markAsPristine();
      } else {
        this.pageService.navigateToPrevious(FALLBACK_BACK_URL, this.activeRoute);
      }
    }
  }

  cancel(): void {
    this.pageService.navigateToPrevious(FALLBACK_BACK_URL, this.activeRoute);
  }

  canSave(): boolean {
    return this.form.dirty;
  }

  protected readonly FormArray = FormArray;
}
